import type { HttpErrorContext } from '@/lib/http';

export interface LaravelErrorJson {
    message: string;
    errors?: Record<string, unknown>;
}

export interface LaravelErrorCause {
    code?: string;
    message?: string;
    url?: string;
    response?: {
        status: number;
        headers?: Headers | Record<string, string>;
        data?: unknown;
    };
}

export default class LaravelError extends Error {
    constructor(message: string, cause: LaravelErrorCause | null = null) {
        super(message);
        this.localCause = cause;
    }

    localCause: LaravelErrorCause | null = null;
    errors: Record<string, string | string[]> = {};

    static fromHttpResponse(context: HttpErrorContext): LaravelError {
        const body = context.body;
        const error = isLaravelErrorJson(body)
            ? this.fromJSON(body)
            : new LaravelError(context.response.statusText || `Request failed with status code ${context.status}`);

        error.localCause = {
            url: context.url,
            response: {
                status: context.status,
                headers: context.response.headers,
                data: body,
            },
        };

        return error;
    }

    static fromTransportError(error: unknown): LaravelError {
        if (typeof DOMException !== 'undefined' && error instanceof DOMException && error.name === 'AbortError') {
            return new LaravelError(error.message || 'The request was cancelled.', {
                code: 'ERR_CANCELED',
                message: error.message,
            });
        }

        if (error instanceof Error) {
            return new LaravelError(error.message, {
                code: 'ERR_NETWORK',
                message: error.message,
            });
        }

        return new LaravelError('Network Error', {
            code: 'ERR_NETWORK',
        });
    }

    static fromJSON(json: LaravelErrorJson): LaravelError {
        const error = new LaravelError(json.message);
        error.errors = normalizeErrors(json.errors);
        error.localCause = null;
        return error;
    }
}

function isLaravelErrorJson(body: unknown): body is LaravelErrorJson {
    return (
        !!body &&
        typeof body === 'object' &&
        'message' in body &&
        typeof (body as { message: unknown }).message === 'string'
    );
}

function normalizeErrors(errors: Record<string, unknown> | undefined): Record<string, string | string[]> {
    if (!errors) {
        return {};
    }

    return Object.fromEntries(
        Object.entries(errors).map(([key, value]) => {
            if (Array.isArray(value)) {
                return [key, value.map(String)];
            }

            return [key, String(value)];
        }),
    );
}
